import React, { useEffect } from 'react';
import { TrendingUpIcon, AlertTriangleIcon, CheckCircleIcon, DownloadIcon, InfoIcon } from 'lucide-react';
interface LimitRow {
  label: string;
  value: string;
  status: 'pass' | 'warn' | 'watch';
  note?: string;
}
const DEMO = {
  totalFunded: 50000000,
  outstanding: 38400000,
  avgDaysToPay: 42.3,
  currentYield: 14.7,
  next30dCashflow: 12800000,
  portfolioHealth: 96.2,
  onTimeRate: 96,
  disputeRatio: 2.1
};
const PORTFOLIO_ROWS = [{
  id: 'INV-8831',
  amount: 124500,
  anchor: 'Premium Textiles',
  payor: 'Target Corp',
  authScore: 98.3,
  payorScore: 96.5,
  verdict: 'APPROVED',
  advance: 85,
  yield: 14.2,
  predicted: '2025-02-15',
  status: 'FUNDED'
}, {
  id: 'INV-8722',
  amount: 92440,
  anchor: 'Global Apparel',
  payor: 'Walmart',
  authScore: 99.1,
  payorScore: 98.2,
  verdict: 'APPROVED',
  advance: 90,
  yield: 14.1,
  predicted: '2025-02-10',
  status: 'SETTLED'
}, {
  id: 'INV-8645',
  amount: 156800,
  anchor: 'Fashion Forward',
  payor: 'HomeGoods',
  authScore: 97.8,
  payorScore: 95.1,
  verdict: 'APPROVED',
  advance: 82,
  yield: 15.1,
  predicted: '2025-02-20',
  status: 'FUNDED'
}, {
  id: 'INV-8512',
  amount: 203400,
  anchor: 'Metro Fabric',
  payor: 'Target Corp',
  authScore: 98.9,
  payorScore: 96.5,
  verdict: 'APPROVED',
  advance: 88,
  yield: 13.5,
  predicted: '2025-02-18',
  status: 'FUNDED'
}];
export function LiquidityDesk() {
  const formatUSD = (n: number) => n >= 1000000 ? `$${(n / 1000000).toFixed(1)}M` : `$${n.toLocaleString()}`;
  useEffect(() => {
    document.title = 'Mint | Portfolio Intelligence';
  }, []);
  return <div className="min-h-screen bg-gray-50" data-id="element-1378">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between" data-id="element-1379">
        <div data-id="element-1380">
          <h1 className="text-3xl font-semibold text-gray-900" data-id="element-1381">
            Portfolio Intelligence
          </h1>
          <p className="text-sm text-gray-600 mt-1" data-id="element-1382">
            View of funded receivables
          </p>
        </div>
        <button className="inline-flex items-center px-3 py-2 bg-white border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50" data-id="element-1383">
          <DownloadIcon size={14} className="mr-1" data-id="element-1384" />
          Export CSV
        </button>
      </div>
      {/* KPI strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-8" data-id="element-1385">
        <TopTile label="Total funded" value={formatUSD(DEMO.totalFunded)} data-id="element-1386" />
        <TopTile label="Outstanding" value={formatUSD(DEMO.outstanding)} data-id="element-1387" />
        <TopTile label="Average days to pay" value={`${DEMO.avgDaysToPay.toFixed(1)}d`} data-id="element-1388" />
        <TopTile label="Current yield" value={`${DEMO.currentYield.toFixed(1)}%`} data-id="element-1389" />
        <TopTile label="Next 30D cashflow" value={formatUSD(DEMO.next30dCashflow)} data-id="element-1390" />
        <TopTile label="Portfolio health" value={`${DEMO.portfolioHealth.toFixed(1)}%`} data-id="element-1391" />
      </div>
      {/* Main grid: left – performance, right – risk snapshot */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8" data-id="element-1392">
        {/* Left column */}
        <div className="xl:col-span-2 space-y-6" data-id="element-1393">
          {/* Performance panel */}
          <CardPanel title="Cashflow and performance" data-id="element-1394">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1395">
              {/* Aging block */}
              <div data-id="element-1396">
                <div className="text-xs text-gray-500 uppercase mb-2" data-id="element-1397">
                  On-time payment rate
                </div>
                <div className="text-2xl font-semibold text-gray-900 mb-3" data-id="element-1398">
                  {DEMO.onTimeRate.toFixed(1)}%
                </div>
                <div className="space-y-2" data-id="element-1399">
                  <AgingRow label="Current" value={72} data-id="element-1400" />
                  <AgingRow label="0–30 days" value={18} data-id="element-1401" />
                  <AgingRow label="30–60 days" value={7} data-id="element-1402" />
                  <AgingRow label="60–90 days" value={2} data-id="element-1403" />
                  <AgingRow label="90+ days" value={1} data-id="element-1404" />
                </div>
              </div>
              {/* Dispute block */}
              <div data-id="element-1405">
                <div className="text-xs text-gray-500 uppercase mb-2" data-id="element-1406">
                  Disputes and dilution
                </div>
                <div className="text-2xl font-semibold text-gray-900 mb-3" data-id="element-1407">
                  {DEMO.disputeRatio.toFixed(1)}%
                  <span className="ml-2 text-xs text-gray-500 font-normal" data-id="element-1408">
                    disputed value
                  </span>
                </div>
                <div className="space-y-2 text-xs" data-id="element-1409">
                  <MetricBarRow label="Quality issues" value={45} data-id="element-1410" />
                  <MetricBarRow label="Quantity mismatch" value={30} data-id="element-1411" />
                  <MetricBarRow label="Pricing error" value={25} data-id="element-1412" />
                </div>
                <div className="mt-3 text-xs text-gray-600 flex items-center" data-id="element-1413">
                  <CheckCircleIcon size={12} className="text-emerald-600 mr-1" data-id="element-1414" />
                  Within internal tolerance band
                </div>
              </div>
            </div>
          </CardPanel>
          {/* Portfolio drill-down table */}
          <CardPanel title="Portfolio drill-down" data-id="element-1415">
            <div className="overflow-x-auto" data-id="element-1416">
              <table className="min-w-full text-xs" data-id="element-1417">
                <thead className="bg-gray-50 text-gray-500 uppercase" data-id="element-1418">
                  <tr data-id="element-1419">
                    <th className="px-4 py-2 text-left" data-id="element-1420">Invoice ID</th>
                    <th className="px-4 py-2 text-left" data-id="element-1421">Amount</th>
                    <th className="px-4 py-2 text-left" data-id="element-1422">Anchor</th>
                    <th className="px-4 py-2 text-left" data-id="element-1423">Payor</th>
                    <th className="px-4 py-2 text-left" data-id="element-1424">Auth score</th>
                    <th className="px-4 py-2 text-left" data-id="element-1425">Payor score</th>
                    <th className="px-4 py-2 text-left" data-id="element-1426">Verdict</th>
                    <th className="px-4 py-2 text-left" data-id="element-1427">Advance</th>
                    <th className="px-4 py-2 text-left" data-id="element-1428">Yield</th>
                    <th className="px-4 py-2 text-left" data-id="element-1429">Predicted</th>
                    <th className="px-4 py-2 text-left" data-id="element-1430">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-1431">
                  {PORTFOLIO_ROWS.map(row => <tr key={row.id} className="hover:bg-gray-50" data-id="element-1432">
                      <td className="px-4 py-3 text-indigo-600 font-medium" data-id="element-1433">
                        {row.id}
                      </td>
                      <td className="px-4 py-3" data-id="element-1434">
                        ${row.amount.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-gray-700" data-id="element-1435">{row.anchor}</td>
                      <td className="px-4 py-3 text-gray-700" data-id="element-1436">{row.payor}</td>
                      <td className="px-4 py-3 text-gray-900" data-id="element-1437">
                        {row.authScore}
                      </td>
                      <td className="px-4 py-3 text-gray-900" data-id="element-1438">
                        {row.payorScore}
                      </td>
                      <td className="px-4 py-3" data-id="element-1439">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-800" data-id="element-1440">
                          {row.verdict}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700" data-id="element-1441">
                        {row.advance}%
                      </td>
                      <td className="px-4 py-3 text-gray-900" data-id="element-1442">{row.yield}%</td>
                      <td className="px-4 py-3 text-gray-600" data-id="element-1443">
                        {row.predicted}
                      </td>
                      <td className="px-4 py-3" data-id="element-1444">
                        <StatusChip status={row.status} data-id="element-1445" />
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </CardPanel>
        </div>
        {/* Right column */}
        <div className="space-y-6" data-id="element-1446">
          <CardPanel title="Risk snapshot" data-id="element-1447">
            <div className="space-y-3 text-sm" data-id="element-1448">
              <RowLabel label="Overall status" value="Compliant" icon={<CheckCircleIcon size={14} className="text-emerald-600" data-id="element-1450" />} data-id="element-1449" />
              <RowLabel label="Concentration" value="Top obligor 18.2% (limit 20%)" icon={<AlertTriangleIcon size={14} className="text-amber-500" data-id="element-1452" />} data-id="element-1451" />
              <RowLabel label="Sector mix" value="Retail 31.0% (limit 35%)" icon={<TrendingUpIcon size={14} className="text-gray-500" data-id="element-1454" />} data-id="element-1453" />
            </div>
          </CardPanel>
          <CardPanel title="Covenant limits" data-id="element-1455">
            <div className="space-y-3 text-sm" data-id="element-1456">
              <LimitRowItem row={{
              label: 'Obligor concentration < 20%',
              value: '18.2%',
              status: 'warn'
            }} data-id="element-1457" />
              <LimitRowItem row={{
              label: 'Top 5 concentration < 60%',
              value: '52.8%',
              status: 'watch'
            }} data-id="element-1458" />
              <LimitRowItem row={{
              label: 'Average tenor < 60 days',
              value: '42.3d',
              status: 'pass'
            }} data-id="element-1459" />
            </div>
          </CardPanel>
          <CardPanel title="Recent alerts" data-id="element-1460">
            <div className="space-y-2 text-xs" data-id="element-1461">
              <AlertRow time="09:42" kind="warn" text="Walmart exposure drifting toward 20% cap" data-id="element-1462" />
              <AlertRow time="09:31" kind="info" text="AI model highlighted dispute uptick for Anchor D (7D window)" data-id="element-1463" />
              <AlertRow time="09:14" kind="info" text="Collection rate rolling 30D improved to 98.7%" data-id="element-1464" />
            </div>
          </CardPanel>
        </div>
      </div>
    </div>;
}
/* Helper components */
function TopTile({
  label,
  value,
  highlight
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-1465">
      <div className="text-[11px] text-gray-500 uppercase tracking-wide mb-1" data-id="element-1466">
        {label}
      </div>
      <div className={`text-xl font-semibold ${highlight ? 'text-emerald-600' : 'text-gray-900'}`} data-id="element-1467">
        {value}
      </div>
    </div>;
}
function CardPanel({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-1468">
      <div className="text-xs font-semibold text-gray-700 uppercase mb-3" data-id="element-1469">
        {title}
      </div>
      {children}
    </div>;
}
function AgingRow({
  label,
  value
}: {
  label: string;
  value: number;
}) {
  return <div className="mb-2" data-id="element-1470">
      <div className="flex items-center justify-between text-[11px] mb-1" data-id="element-1471">
        <span className="text-gray-600" data-id="element-1472">{label}</span>
        <span className="text-gray-800 font-medium" data-id="element-1473">{value}%</span>
      </div>
      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-1474">
        <div className="h-full bg-gray-700" style={{
        width: `${value}%`
      }} data-id="element-1475"></div>
      </div>
    </div>;
}
function MetricBarRow({
  label,
  value
}: {
  label: string;
  value: number;
}) {
  return <div data-id="element-1476">
      <div className="flex items-center justify-between text-[11px] mb-1" data-id="element-1477">
        <span className="text-gray-600" data-id="element-1478">{label}</span>
        <span className="text-gray-800 font-medium" data-id="element-1479">{value}%</span>
      </div>
      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-1480">
        <div className="h-full bg-gray-500" style={{
        width: `${value}%`
      }} data-id="element-1481"></div>
      </div>
    </div>;
}
function StatusChip({
  status
}: {
  status: string;
}) {
  const base = 'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium';
  if (status === 'SETTLED') {
    return <span className={`${base} bg-gray-100 text-gray-800`} data-id="element-1482">{status}</span>;
  }
  return <span className={`${base} bg-gray-100 text-gray-800`} data-id="element-1483">{status}</span>;
}
function RowLabel({
  label,
  value,
  icon
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
}) {
  return <div className="flex items-start justify-between" data-id="element-1484">
      <div className="text-xs text-gray-600" data-id="element-1485">{label}</div>
      <div className="flex items-center text-xs text-gray-900" data-id="element-1486">
        {icon && <span className="mr-1" data-id="element-1487">{icon}</span>}
        <span data-id="element-1488">{value}</span>
      </div>
    </div>;
}
function LimitRowItem({
  row
}: {
  row: LimitRow;
}) {
  return <div className="flex items-center justify-between border border-gray-100 rounded-md px-3 py-2 bg-gray-50" data-id="element-1489">
      <div className="flex-1" data-id="element-1490">
        <div className="text-xs text-gray-600" data-id="element-1491">{row.label}</div>
        {row.note && <div className="text-[11px] text-gray-500 mt-1" data-id="element-1492">{row.note}</div>}
      </div>
      <div className="flex items-center gap-2" data-id="element-1493">
        <span className="text-sm font-semibold text-gray-900" data-id="element-1494">{row.value}</span>
        <LimitStatus status={row.status} data-id="element-1495" />
      </div>
    </div>;
}
function LimitStatus({
  status
}: {
  status: 'pass' | 'warn' | 'watch';
}) {
  const base = 'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium';
  if (status === 'pass') {
    return <span className={`${base} bg-gray-100 text-gray-800`} data-id="element-1496">Pass</span>;
  }
  if (status === 'warn') {
    return <span className={`${base} bg-gray-100 text-gray-800`} data-id="element-1497">Warn</span>;
  }
  return <span className={`${base} bg-gray-900 text-white`} data-id="element-1498">Watch</span>;
}
function AlertRow({
  time,
  kind,
  text
}: {
  time: string;
  kind: 'info' | 'warn' | 'breach';
  text: string;
}) {
  const icon = kind === 'breach' ? <AlertTriangleIcon size={12} className="text-red-600" data-id="element-1499" /> : kind === 'warn' ? <AlertTriangleIcon size={12} className="text-amber-500" data-id="element-1500" /> : <InfoIcon size={12} className="text-blue-500" data-id="element-1501" />;
  return <div className="flex items-start gap-2 border-b border-gray-100 pb-2 last:border-b-0 last:pb-0" data-id="element-1502">
      <span className="text-gray-500 w-10" data-id="element-1503">{time}</span>
      <span data-id="element-1504">{icon}</span>
      <span className="text-gray-800" data-id="element-1505">{text}</span>
    </div>;
}